import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import { Container, Paper, Typography, Button, Box, Alert, IconButton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { FormTextField } from '../components/common/FormFields';
import { resetPasswordSchema } from '../services/utils/validation';
import { AuthService } from '../services';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

interface ResetPasswordValues {
    password: string;
    confirmPassword: string;
}

const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const { token } = useParams<{ token: string }>();
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();

    const initialValues: ResetPasswordValues = {
        password: '',
        confirmPassword: ''
    };

    const handleSubmit = async (values: ResetPasswordValues, { setSubmitting }: any) => {
        try {
            setError(null);
            if (!token) {
                throw new Error('Reset token is missing');
            }
            await AuthService.resetPassword(token, values.password);
            navigate('/login', { state: { message: t('auth.resetPasswordSuccess') } });
        } catch (err: any) {
            setError(err.response?.data?.error || t('auth.resetPasswordError'));
            setSubmitting(false);
        }
    };

    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 8, mb: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography component="h1" variant="h5" align="center" gutterBottom>
                        {t('auth.resetPassword')}
                    </Typography>

                    {error && (
                        <Alert
                            severity="error"
                            action={
                                <IconButton
                                    aria-label="close"
                                    color="inherit"
                                    size="small"
                                    onClick={() => setError(null)}
                                >
                                    <CloseIcon fontSize="inherit" />
                                </IconButton>
                            }
                            sx={{ mb: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={resetPasswordSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form>
                                <FormTextField
                                    name="password"
                                    label={t('auth.password')}
                                    type="password"
                                    required
                                    fullWidth
                                    sx={{ mb: 2 }}
                                />
                                <FormTextField
                                    name="confirmPassword"
                                    label={t('auth.confirmPassword')}
                                    type="password"
                                    required
                                    fullWidth
                                    sx={{ mb: 3 }}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t('common.loading') : t('auth.resetPassword')}
                                </Button>
                            </Form>
                        )}
                    </Formik>
                </Paper>
            </Box>
        </Container>
    );
};

export default ResetPassword;
